import React from "react";
import { X, Crown, Check, ArrowRight, Sparkle } from "@phosphor-icons/react";

/**
 * Modal that pops up when a user tries to take an action with 0 credits.
 * Displays a 3-plan comparison inline and routes to full Pricing page.
 * Also used for "you used all your free trial credits" moments.
 */
export default function UpgradeModal({ open, onClose, onViewPricing, reason }) {
  if (!open) return null;

  const HIGHLIGHTS = [
    { plan: "Creator", price: "$19.99", credits: "100 credits/mo", line: "Perfect for solo creators" },
    { plan: "Pro", price: "$39.99", credits: "300 credits/mo", line: "Bulk Create + Priority", featured: true },
    { plan: "Business", price: "$79.99", credits: "700 credits/mo", line: "Content Calendar + priority support" },
  ];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="upgrade-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-[#E91E8C] via-pink-500 to-amber-400 text-white p-6 relative">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/20"
            data-testid="upgrade-modal-close"
          >
            <X size={18} />
          </button>
          <Crown size={32} weight="fill" className="mb-2" />
          <h2 className="text-2xl font-bold mb-1">
            {reason === "out_of_credits"
              ? "You're out of credits!"
              : "Ready to scale your content?"}
          </h2>
          <p className="text-sm text-white/90">
            {reason === "out_of_credits"
              ? "You used your free trial. Pick a plan to keep creating without limits."
              : "Unlock unlimited content with your own AI team — pick your plan and start creating."}
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            {HIGHLIGHTS.map((p) => (
              <button
                key={p.plan}
                onClick={onViewPricing}
                className={`text-left p-4 rounded-xl border-2 transition-all hover:-translate-y-0.5 ${
                  p.featured
                    ? "border-[#E91E8C] bg-[#E91E8C]/5 shadow-md"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                {p.featured && (
                  <div className="inline-block bg-[#E91E8C] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider mb-2">
                    Popular
                  </div>
                )}
                <div className="font-bold text-zinc-900">{p.plan}</div>
                <div className="text-2xl font-bold text-zinc-900 mt-1">{p.price}<span className="text-xs font-normal text-zinc-500">/mo</span></div>
                <div className="text-xs text-[#E91E8C] font-semibold mt-1">{p.credits}</div>
                <div className="text-[11px] text-zinc-500 mt-1 leading-tight">{p.line}</div>
              </button>
            ))}
          </div>

          <div className="space-y-2 text-sm text-zinc-600 mb-5">
            {[
              "Full access to every AI tool (Script Studio, AI Twin, Scene Studio, Bulk Create, Auto Create)",
              "Bring your own Magic Hour, ElevenLabs, muapi.ai keys — no markup on usage",
              "Cancel anytime, no annual commitment",
            ].map((line) => (
              <div key={line} className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[#E91E8C] mt-0.5 flex-shrink-0" />
                <span>{line}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={onViewPricing}
              data-testid="upgrade-modal-view-pricing"
              className="flex-1 flex items-center justify-center gap-1.5 bg-[#E91E8C] text-white font-semibold py-3 rounded-lg hover:bg-[#c91876] transition-colors"
            >
              <Sparkle size={16} weight="fill" /> Compare all plans <ArrowRight size={14} />
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 rounded-lg border border-zinc-200 text-sm text-zinc-600 hover:bg-zinc-50"
              data-testid="upgrade-modal-maybe-later"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
